import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch, useAppSelector , useModal } from '@store/hooks';

import { MODAL_TYPES } from '../../../components/modals/constants';
import { FRONTEND_APP_VERSION } from '../../../constants';
import { useThemeContext } from '../../../context/theme/context';
import SentryService from '../../../services/SentryService';
import { trackEvent } from '../../../utils/analytics_utils';
import { saveUserInfoToLocalStorage } from '../../../utils/LocalStorageUtils';
import { compareVersions } from '../../../utils/utils';
import { type TutorialProgress } from '../types';
import { Auth0ErrorHandler } from '../utils/auth-error-handler';

import {
  useAddTroubleWordsMutation,
  useCopyPublicTemplateToUserMutation,
  useCopyUserTemplateMutation,
  useGetLanguagesAndPreferencesQuery,
  useGetTemplatesQuery,
  useGetTroubleWordsQuery,
  useGetTutorialProgressQuery,
  useGetUserInfoQuery,
  useLazyGetAvailableSpecialtiesQuery,
  useLazyGetTemplatesQuery,
  useLazyGetUserInfoQuery,
  useToggleEhrModeMutation,
  useToggleFeatureFlagMutation,
  useUpdateDefaultTimezoneMutation,
  useUpdateLastVersionUsedMutation,
  useUpdateOnboardingStepsMutation,
  useUpdateTutorialProgressMutation,
  useUserLoginMutation,
} from './api';
import {
  selectAuthStatus,
  selectAvailableSpecialties,
  selectAwaitingLogin,
  selectIsAuthenticatedFromState,
  selectLoading,
  selectNewFeature,
  selectSubjectLanguage,
  selectSubjectLanguageVariants,
  selectUnauthorized,
} from './selectors';
import {
  resetUserState,
  setAuthState,
  setAuthStatus,
  setAvailableSpecialties,
  setIsAuthenticated,
  setNewFeature as setNewFeatureAction,
} from './slice';
import { type OnboardingSteps } from './types';

// Module-level flag to ensure version update modal only opens once across ALL component instances
let _versionModalOpened = false;

/**
 * Utility function to handle API errors consistently
 */
function handleApiError(error: unknown, context: string): string {
  console.error(`${context}:`, error);

  if (error && typeof error === 'object' && 'status' in error) {
    const apiError = error as { status: number; data?: { message?: string; error?: string } };

    // Return user-friendly messages based on status code
    if (apiError.status === 400) {
      return apiError.data?.message || 'Invalid request';
    } else if (apiError.status === 404) {
      return 'Resource not found';
    } else if (apiError.status === 409) {
      return 'Resource already exists';
    } else if (apiError.status === 429) {
      return 'Too many requests. Please try again later';
    } else if (apiError.status >= 500) {
      return 'Server error. Please try again later';
    }

    return apiError.data?.message || apiError.data?.error || 'An error occurred';
  }

  return 'An unexpected error occurred';
}

/**
 * Hook for Auth0 access token management
 */
export function useAccessToken() {
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const accessTokenRef = useRef<string>();
  const dispatch = useAppDispatch();

  const tokenExpired = useCallback((token: string): boolean => {
    try {
      interface JWTPayload {
        exp: number;
      }
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      const payload = JSON.parse(atob(parts[1])) as JWTPayload;
      const expirationTimeInSeconds = payload.exp;
      const nowInSeconds = Date.now() / 1000;
      return nowInSeconds >= expirationTimeInSeconds;
    } catch {
      // Malformed token
      return true;
    }
  }, []);

  const checkAndRefreshToken = useCallback(async (): Promise<string | null> => {
    try {
      let token = accessTokenRef.current;

      // If there's no token or it's expired, refresh it
      if (!token || tokenExpired(token)) {
        const response = await getAccessTokenSilently({
          detailedResponse: true,
        });
        token = response.access_token;
        localStorage.setItem('accessToken', response.access_token);
        accessTokenRef.current = response.access_token;
      }

      return token;
    } catch (error) {
      // Use centralized error handler
      const errorResponse = Auth0ErrorHandler.analyze(error);

      // Clean up tokens if needed
      Auth0ErrorHandler.cleanup(errorResponse, accessTokenRef);

      // Try popup if recommended
      if (errorResponse.shouldRetryWithPopup) {
        try {
          const token = await getAccessTokenWithPopup();
          if (token) {
            localStorage.setItem('accessToken', token);
            accessTokenRef.current = token;
            // Success! User is authenticated
            return token;
          }
        } catch (popupError) {
          // Popup failed - analyze this error too
          const popupErrorResponse = Auth0ErrorHandler.analyze(popupError);
          Auth0ErrorHandler.cleanup(popupErrorResponse, accessTokenRef);

          // ALWAYS set awaitingLogin when we've exhausted all options
          dispatch(setAuthState({ awaitingLogin: true }));
          return null;
        }
      }

      // No popup retry or popup wasn't recommended - this is a final failure
      // ALWAYS set awaitingLogin to prevent stuck state
      dispatch(setAuthState({ awaitingLogin: true }));
      return null;
    }
  }, [getAccessTokenSilently, getAccessTokenWithPopup, tokenExpired, dispatch]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    const accessToken = await checkAndRefreshToken();
    return accessToken || '';
  }, [checkAndRefreshToken]);

  return { getAccessToken, checkAndRefreshToken };
}

// Rate limiting for userLogin calls
const USER_LOGIN_LAST_ATTEMPT_KEY = 'userLoginLastAttempt';
const USER_LOGIN_MIN_INTERVAL_MS = 5000; // 5 seconds between attempts

function canAttemptUserLogin(): boolean {
  const now = Date.now();
  const lastAttempt = localStorage.getItem(USER_LOGIN_LAST_ATTEMPT_KEY);

  if (!lastAttempt) {
    localStorage.setItem(USER_LOGIN_LAST_ATTEMPT_KEY, now.toString());
    return true;
  }

  const timeSinceLastAttempt = now - parseInt(lastAttempt, 10);

  if (timeSinceLastAttempt >= USER_LOGIN_MIN_INTERVAL_MS) {
    localStorage.setItem(USER_LOGIN_LAST_ATTEMPT_KEY, now.toString());
    return true;
  }

  return false;
}

/**
 * Hook for user authentication state
 */
export function useUserAuth() {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectLoading);
  const awaitingLogin = useAppSelector(selectAwaitingLogin);
  const unauthorized = useAppSelector(selectUnauthorized);
  const isAuthenticated = useAppSelector(selectIsAuthenticatedFromState);
  const { getAccessToken, checkAndRefreshToken } = useAccessToken();
  const [userLogin] = useUserLoginMutation();
  const loginAttemptedRef = useRef(false);

  const initAuth = useCallback(async () => {
    // Prevent concurrent initialization
    if (loginAttemptedRef.current) {
      return;
    }

    dispatch(setAuthState({ loading: true }));

    const tokenResult = await checkAndRefreshToken();
    const token = tokenResult || localStorage.getItem('accessToken');

    if (token) {
      dispatch(setAuthState({ awaitingLogin: false, loading: false }));
      dispatch(setIsAuthenticated(true));

      // Log the user login with rate limiting
      if (canAttemptUserLogin()) {
        loginAttemptedRef.current = true;
        try {
          await userLogin({ device_info: { platform: 'web' } }).unwrap();
          // Reset on success
          localStorage.removeItem(USER_LOGIN_LAST_ATTEMPT_KEY);
        } catch (error) {
          SentryService.logEvent('Failed to log user login', {
            level: 'error',
            tags: { type: 'api_error', endpoint: 'userLogin' },
            extra: { error },
          });
          // Don't reset loginAttemptedRef on error to prevent retries in the same session
          // Only log the error, don't change auth state
        }
      } else {
        SentryService.logEvent('User login rate limited - too many attempts', {
          level: 'warning',
          tags: { type: 'rate_limit', endpoint: 'userLogin' },
          extra: { lastAttempt: localStorage.getItem(USER_LOGIN_LAST_ATTEMPT_KEY) },
        });
      }
    } else {
      dispatch(setAuthState({ awaitingLogin: true, loading: false }));
      dispatch(setIsAuthenticated(false));
      dispatch(resetUserState());
      // Reset the login attempted flag when user needs to re-authenticate
      loginAttemptedRef.current = false;
    }
  }, [dispatch, checkAndRefreshToken, userLogin]);

  return {
    loading,
    awaitingLogin,
    unauthorized,
    isAuthenticated,
    getAccessToken,
    initAuth,
  };
}

/**
 * Hook for user onboarding
 */
export function useUserOnboarding(options?: { skip?: boolean }) {
  const [updateOnboardingSteps] = useUpdateOnboardingStepsMutation();
  const { data: userInfoData, error } = useGetUserInfoQuery(undefined, options);
  const onboardingSteps = userInfoData?.onboarding_steps || ({} as OnboardingSteps);

  const updateOnboardingStep = useCallback(
    async (step: keyof OnboardingSteps, value: boolean) => {
      try {
        await updateOnboardingSteps({ [step]: value }).unwrap();
        // RTK Query will automatically update the cache
        return true;
      } catch (error) {
        handleApiError(error, 'Failed to update onboarding step');
        return false;
      }
    },
    [updateOnboardingSteps],
  );

  const completeSignup = useCallback(() => {
    void updateOnboardingStep('account_creation_onboarding_completed', true);
  }, [updateOnboardingStep]);

  return {
    updateOnboardingStep,
    completeSignup,
    onboardingSteps,
    error,
  };
}

/**
 * Hook for user templates
 */
export function useUserTemplates(options?: { skip?: boolean }) {
  const { data: templatesData, error } = useGetTemplatesQuery(undefined, options);
  const templates = templatesData?.templates || [];
  const [getTemplates] = useLazyGetTemplatesQuery();
  const [copyUserTemplate] = useCopyUserTemplateMutation();
  const [copyPublicTemplate] = useCopyPublicTemplateToUserMutation();

  const refreshTemplatesList = useCallback(async () => {
    try {
      await getTemplates(undefined).unwrap();
      return true;
    } catch (error) {
      handleApiError(error, 'Failed to refresh templates');
      return false;
    }
  }, [getTemplates]);

  const addSharedTemplate = useCallback(
    async (templateId: string) => {
      try {
        if (templateId.startsWith('u:')) {
          await copyUserTemplate({ template_id: templateId }).unwrap();
        } else if (templateId.startsWith('p:')) {
          await copyPublicTemplate({ template_id: templateId }).unwrap();
        }
        await refreshTemplatesList();
        localStorage.removeItem('sharedTemplateId');
        localStorage.setItem('sharedTemplateAdded', templateId);
        return true;
      } catch (error) {
        handleApiError(error, 'Failed to add shared template');
        // The error has been logged and could be shown to the user if needed
        return false;
      }
    },
    [copyUserTemplate, copyPublicTemplate, refreshTemplatesList],
  );

  return {
    templatesList: templates,
    refreshTemplatesList,
    addSharedTemplate,
    error,
  };
}

/**
 * Hook for trouble words management
 */
export function useUserTroubleWords(options?: { skip?: boolean }) {
  const { data: troubleWordsData, error } = useGetTroubleWordsQuery(undefined, options);
  const troubleWords = troubleWordsData?.trouble_words || [];
  const [addTroubleWordsMutation] = useAddTroubleWordsMutation();

  const addTroubleWords = useCallback(
    async (
      newTroubleWords: { correct_spelling: string; misspelling: string }[],
    ): Promise<boolean> => {
      try {
        await addTroubleWordsMutation({ trouble_words: newTroubleWords }).unwrap();
        return true;
      } catch (error) {
        handleApiError(error, 'Failed to add trouble words');
        return false;
      }
    },
    [addTroubleWordsMutation],
  );

  return {
    troubleWords,
    addTroubleWords,
    error,
  };
}

/**
 * Hook for tutorial progress
 */
export function useUserTutorials(options?: { skip?: boolean }) {
  const { data: tutorialProgressData, error } = useGetTutorialProgressQuery(undefined, options);
  const tutorialProgress = tutorialProgressData?.tutorial_progress || {};
  const [updateTutorialProgressMutation] = useUpdateTutorialProgressMutation();

  const updateTutorialProgress = useCallback(
    async (tutorialId: string, updatedFields: Partial<TutorialProgress>): Promise<boolean> => {
      try {
        await updateTutorialProgressMutation({
          tutorial_id: tutorialId,
          updated_fields: updatedFields,
        }).unwrap();
        return true;
      } catch (error) {
        handleApiError(error, 'Error updating tutorial progress');
        return false;
      }
    },
    [updateTutorialProgressMutation],
  );

  return {
    tutorialProgress,
    updateTutorialProgress,
    error,
  };
}

/**
 * Hook for feature flags
 */
export function useUserFeatureFlags(options?: { skip?: boolean }) {
  const { data: userInfoData, error } = useGetUserInfoQuery(undefined, options);
  const featureFlags = userInfoData?.feature_flags || {};
  const [toggleFeatureFlagMutation] = useToggleFeatureFlagMutation();
  const [toggleEhrModeMutation] = useToggleEhrModeMutation();

  const toggleFeatureFlag = useCallback(
    async (featureName: string, enable: boolean): Promise<boolean> => {
      try {
        await toggleFeatureFlagMutation({
          feature_name: featureName,
          enable,
        }).unwrap();
        return true;
      } catch (error) {
        handleApiError(error, `Error toggling feature flag ${featureName}`);
        return false;
      }
    },
    [toggleFeatureFlagMutation],
  );

  const toggleEhrMode = useCallback(
    async (enable: boolean): Promise<boolean> => {
      try {
        await toggleEhrModeMutation({ enable }).unwrap();
        return true;
      } catch (error) {
        handleApiError(error, 'Error toggling EHR mode');
        return false;
      }
    },
    [toggleEhrModeMutation],
  );

  return {
    featureFlags,
    toggleFeatureFlag,
    toggleEhrMode,
    error,
  };
}

/**
 * Hook for available specialties
 */
export function useAvailableSpecialties() {
  const dispatch = useAppDispatch();
  const availableSpecialties = useAppSelector(selectAvailableSpecialties);
  const [fetchSpecialties] = useLazyGetAvailableSpecialtiesQuery();

  const loadAvailableSpecialties = useCallback(async () => {
    try {
      const result = await fetchSpecialties(undefined).unwrap();
      dispatch(setAvailableSpecialties(result.specialties));
    } catch (error) {
      handleApiError(error, 'Failed to fetch available specialties');
    }
  }, [fetchSpecialties, dispatch]);

  return {
    availableSpecialties,
    loadAvailableSpecialties,
  };
}

/**
 * Main useUser hook that provides all user-related functionality
 * This hook is designed to work similarly to the existing useUser from context
 */
export function useUser() {
  const dispatch = useAppDispatch();
  const {
    user: auth0User,
    getIdTokenClaims,
    isAuthenticated: isAuth0Authenticated,
    isLoading: isAuth0Loading,
  } = useAuth0();
  const { setBrand } = useThemeContext();

  // Selectors
  const loading = useAppSelector(selectLoading);
  const awaitingLogin = useAppSelector(selectAwaitingLogin);
  const unauthorized = useAppSelector(selectUnauthorized);
  const newFeature = useAppSelector(selectNewFeature);
  const authStatus = useAppSelector(selectAuthStatus);

  const shouldSkipQueries = !isAuth0Authenticated;

  // Hooks
  const { getAccessToken } = useUserAuth();
  const { open: openModal } = useModal();
  const { updateOnboardingStep, completeSignup } = useUserOnboarding({ skip: shouldSkipQueries });
  const {
    templatesList,
    refreshTemplatesList,
    addSharedTemplate,
    error: templatesError,
  } = useUserTemplates({ skip: shouldSkipQueries });
  const {
    troubleWords,
    addTroubleWords,
    error: troubleWordsError,
  } = useUserTroubleWords({ skip: shouldSkipQueries });
  const { updateTutorialProgress, error: tutorialError } = useUserTutorials({
    skip: shouldSkipQueries,
  });
  const { toggleFeatureFlag, toggleEhrMode } = useUserFeatureFlags({ skip: shouldSkipQueries });
  const { loadAvailableSpecialties } = useAvailableSpecialties();

  // API hooks - these will automatically fetch and cache data
  const {
    data: userInfoData,
    error: userInfoError,
    isError: userInfoIsError,
  } = useGetUserInfoQuery(undefined, { skip: shouldSkipQueries });
  const { data: _languagesData, error: languagesError } = useGetLanguagesAndPreferencesQuery(
    undefined,
    { skip: shouldSkipQueries },
  );

  const [fetchUserInfo] = useLazyGetUserInfoQuery();
  const [updateLastVersionUsed] = useUpdateLastVersionUsedMutation();
  const [updateDefaultTimezone] = useUpdateDefaultTimezoneMutation();

  // Memoize the openModal function to prevent unnecessary re-renders
  const stableOpenModal = useCallback(
    (modalType: Parameters<typeof openModal>[0], modalProps?: Parameters<typeof openModal>[1]) => {
      openModal(modalType, modalProps);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Memoize loadAvailableSpecialties
  const stableLoadAvailableSpecialties = useCallback(() => {
    void loadAvailableSpecialties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoize updateDefaultTimezone
  const stableUpdateDefaultTimezone = useCallback(
    (timezone: string) => {
      void updateDefaultTimezone({ timezone });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Handle user info updates
  useEffect(() => {
    if (userInfoData) {
      const win: Window = window;
      const data = userInfoData;

      saveUserInfoToLocalStorage(data.user_info, auth0User);

      // Handle next/main URLs
      const isOriginInList = (urls: string, origin: string): boolean => {
        return urls
          .split(',')
          .map(url => url.trim())
          .includes(origin);
      };

      if (data.feature_flags && data.feature_flags.next) {
        const nextUrls = String(import.meta.env.VITE_NEXT_URLS || '');
        if (!isOriginInList(nextUrls, win.location.origin)) {
          // Switching user to next - preserve full path including search and hash
          const firstUrl = nextUrls.split(',')[0].trim();
          win.location.href =
            firstUrl + win.location.pathname + win.location.search + win.location.hash;
          return;
        }
      } else {
        const mainUrls = String(import.meta.env.VITE_MAIN_URLS || '');
        if (!isOriginInList(mainUrls, win.location.origin)) {
          // Switching user to main - preserve full path including search and hash
          const firstUrl = mainUrls.split(',')[0].trim();
          win.location.href =
            firstUrl + win.location.pathname + win.location.search + win.location.hash;
          return;
        }
      }

      // Update brand
      if (data.brand && ['jotpsych', 'jotpal', 'smartscribe'].includes(data.brand)) {
        localStorage.setItem('preferred_brand', data.brand);
        setBrand(data.brand);
      }

      // Track user ID
      if (data.user_id) {
        trackEvent({
          event: 'userID',
          user_id: data.user_id,
        });
      }

      // Check version and update if needed
      // Show version update modal when user's last version is less than current version
      if (
        !_versionModalOpened &&
        data.last_version_used &&
        data.last_version_used.frontend &&
        compareVersions(data.last_version_used.frontend, FRONTEND_APP_VERSION) < 0
      ) {
        _versionModalOpened = true;
        // Open the version update modal with the current version
        stableOpenModal(MODAL_TYPES.VERSION_UPDATE, {
          version: FRONTEND_APP_VERSION,
        });
        // Update the last version used in the backend
        void updateLastVersionUsed({ platform: 'frontend', version: FRONTEND_APP_VERSION });
      }

      // Check if signup is needed

      if (!data.user_info || !data.user_info.name) {
        // Load available specialties for signup
        stableLoadAvailableSpecialties();
      }

      // Update timezone if needed
      if (!data.default_timezone) {
        // No default timezone found, sending to server
        stableUpdateDefaultTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      }
    }
  }, [
    userInfoData,
    auth0User,
    dispatch,
    setBrand,
    updateLastVersionUsed,
    stableOpenModal,
    stableLoadAvailableSpecialties,
    stableUpdateDefaultTimezone,
  ]);

  // Set Sentry user context
  useEffect(() => {
    const setSentryContext = async () => {
      if (userInfoData?.user_id) {
        const claims = await getIdTokenClaims();
        if (claims) {
          SentryService.setUserContext({
            id: claims.sub as string,
            email: claims.email as string,
            username: claims.name as string,
          });
        }
      }
    };
    void setSentryContext();
  }, [userInfoData?.user_id, getIdTokenClaims]);

  // Handle shared template
  useEffect(() => {
    if (userInfoData && localStorage.getItem('sharedTemplateId')) {
      const sharedTemplateId = localStorage.getItem('sharedTemplateId');
      if (sharedTemplateId) {
        void addSharedTemplate(sharedTemplateId);
      }
    }
  }, [userInfoData, addSharedTemplate]);

  const fetchUserInfoWrapper = useCallback(async () => {
    try {
      await fetchUserInfo(undefined).unwrap();
    } catch (error) {
      handleApiError(error, 'Failed to fetch user info');
      dispatch(setAuthState({ unauthorized: true }));
    }
  }, [fetchUserInfo, dispatch]);

  const setNewFeature = useCallback(
    (value: boolean) => {
      dispatch(setNewFeatureAction(value));
    },
    [dispatch],
  );

  // Initial auth status determination based on Auth0
  useEffect(() => {
    if (isAuth0Loading) {
      dispatch(setAuthStatus('initializing'));
      return;
    }

    if (!isAuth0Authenticated) {
      dispatch(setAuthStatus('unauthenticated'));
    } else {
      // If authenticated, let the API check effect handle it
      // But we need to trigger checking status
      if (authStatus === 'initializing') {
        dispatch(setAuthStatus('checking'));
      }
    }
  }, [isAuth0Authenticated, isAuth0Loading, dispatch, authStatus]);

  // Manage auth status based on API state
  useEffect(() => {
    // Skip this logic if queries are being skipped
    if (shouldSkipQueries) {
      return;
    }

    // Start with checking status when query is loading
    if (!userInfoData && !userInfoIsError && !userInfoError) {
      dispatch(setAuthStatus('checking'));
      return;
    }

    // Handle errors
    if (userInfoIsError || userInfoError) {
      // Check if Auth0 thinks the user is authenticated
      // If Auth0 says authenticated but API returns error, it's likely an expired token
      // If Auth0 says not authenticated, user is logged out

      if (isAuth0Authenticated) {
        // Auth0 thinks user is authenticated but API call failed
        // This is likely an expired token scenario
        dispatch(setAuthStatus('unauthorized'));
      } else {
        // Auth0 says not authenticated - show login view
        dispatch(setAuthStatus('unauthenticated'));
      }
      return;
    }

    // User data is available - determine auth status
    if (userInfoData) {
      // Check if user is locked out due to payment issues
      const isLockedOut =
        userInfoData.payment_status === 'paused' ||
        userInfoData.payment_status === 'canceled' ||
        userInfoData.payment_status === 'unpaid' ||
        userInfoData.payment_status === 'incomplete_expired';

      if (isLockedOut) {
        dispatch(setAuthStatus('locked_out'));
        return;
      }

      // User is fully authenticated
      dispatch(setAuthStatus('authenticated'));
    } else {
      // No user data available - user needs to login
      dispatch(setAuthStatus('unauthenticated'));
    }
  }, [
    userInfoData,
    userInfoIsError,
    userInfoError,
    dispatch,
    isAuth0Authenticated,
    shouldSkipQueries,
  ]);

  // Get values from RTK Query data
  const lockedOut =
    userInfoData?.payment_status === 'paused' ||
    userInfoData?.payment_status === 'canceled' ||
    userInfoData?.payment_status === 'unpaid' ||
    userInfoData?.payment_status === 'incomplete_expired' ||
    false;

  const subjectLanguage = _languagesData?.user_preferences?.subject || 'patient';

  // Create compatibility layer for camelCase properties
  const userState = useMemo(() => {
    if (!userInfoData) return null;

    // Transform snake_case to camelCase for backward compatibility
    return {
      // Core properties
      user_id: userInfoData.user_id,
      userInfo: userInfoData.user_info,

      // Transform snake_case to camelCase
      featureFlags: userInfoData.feature_flags,
      paymentStatus: userInfoData.payment_status,
      emailVerified: userInfoData.email_verified,
      onboardingSteps: userInfoData.onboarding_steps,
      groupUserRoles: userInfoData.group_user_roles,
      groupIds: userInfoData.group_ids,
      gamification: userInfoData.gamification,
      referralInfo: userInfoData.referral_info,
      userMetrics: userInfoData.user_metrics,
      stripeInfo: userInfoData.stripe_info,
      lastVersionUsed: userInfoData.last_version_used,
      patientMatchEnrolled: userInfoData.patient_match_enrolled,
      providerBillingInfo: userInfoData.provider_billing_info,
      firstLoginTime: userInfoData.first_login_time,
      clearinghouseLinked: userInfoData.clearinghouse_linked,
      defaultTimezone: userInfoData.default_timezone,

      // Keep snake_case versions for gradual migration
      feature_flags: userInfoData.feature_flags,
      payment_status: userInfoData.payment_status,
      email_verified: userInfoData.email_verified,
      onboarding_steps: userInfoData.onboarding_steps,
      group_user_roles: userInfoData.group_user_roles,
      group_ids: userInfoData.group_ids,
      referral_info: userInfoData.referral_info,
      user_metrics: userInfoData.user_metrics,
      stripe_info: userInfoData.stripe_info,
      last_version_used: userInfoData.last_version_used,
      patient_match_enrolled: userInfoData.patient_match_enrolled,
      provider_billing_info: userInfoData.provider_billing_info,
      first_login_time: userInfoData.first_login_time,
      clearinghouse_linked: userInfoData.clearinghouse_linked,
      default_timezone: userInfoData.default_timezone,
    };
  }, [userInfoData]);

  // Aggregate error states
  const hasError =
    userInfoIsError ||
    !!templatesError ||
    !!troubleWordsError ||
    !!languagesError ||
    !!tutorialError;
  const errors = {
    userInfo: userInfoError,
    templates: templatesError,
    troubleWords: troubleWordsError,
    languages: languagesError,
    tutorial: tutorialError,
  };

  return {
    userState,
    newFeature,
    setNewFeature,
    awaitingLogin,
    completeSignup,
    unauthorized,
    getAccessToken,
    updateOnboardingStep,
    loading,
    templatesList,
    refreshTemplatesList,
    lockedOut,
    troubleWords,
    addTroubleWords,
    subjectLanguage,
    fetchUserInfo: fetchUserInfoWrapper,
    updateTutorialProgress,
    toggleFeatureFlag,
    toggleEhrMode,
    hasError,
    errors,
  };
}

/**
 * Selector-driven utility hooks for convenient access to user state
 * These hooks provide a cleaner API compared to using useAppSelector directly
 */

// Auth state hooks
export const useAuthStatus = () => useAppSelector(selectAuthStatus);
export const useLoading = () => useAppSelector(selectLoading);
export const useAwaitingLogin = () => useAppSelector(selectAwaitingLogin);
export const useUnauthorized = () => useAppSelector(selectUnauthorized);
export const useIsAuthenticated = () => useAppSelector(selectIsAuthenticatedFromState);

// User info hooks
export const useUserId = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.user_id;
};
export const useUserInfo = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.user_info;
};
export const useUserName = () => {
  const { data } = useGetUserInfoQuery(undefined);

  return data?.user_info?.name || '';
};
export const useUserEmail = () => {
  const { data } = useGetUserInfoQuery(undefined);

  return data?.user_info?.email || '';
};
export const useUserRoles = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.group_user_roles || [];
};
export const useEmailVerified = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.email_verified;
};

// Payment and account status hooks
export const usePaymentStatus = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.payment_status;
};
export const useLockedOut = () => {
  const { data } = useGetUserInfoQuery(undefined);
  const status = data?.payment_status;
  return (
    status === 'paused' ||
    status === 'canceled' ||
    status === 'unpaid' ||
    status === 'incomplete_expired'
  );
};

// Feature flags hooks
export const useFeatureFlags = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.feature_flags || {};
};
export const useFeatureFlag = (flagName: string) => {
  const { data } = useGetUserInfoQuery(undefined);

  return data?.feature_flags?.[flagName] || false;
};
export const useNewFeature = () => useAppSelector(selectNewFeature);

// Groups and roles hooks
export const useGroupUserRoles = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.group_user_roles || [];
};
export const useGroupIds = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.group_ids || [];
};

// Onboarding hooks
export const useOnboardingSteps = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.onboarding_steps || ({} as OnboardingSteps);
};
export const useOnboardingStep = (stepName: keyof OnboardingSteps) => {
  const { data } = useGetUserInfoQuery(undefined);

  return data?.onboarding_steps?.[stepName] || false;
};

// Tutorial progress hooks
export const useTutorialProgress = () => {
  const { data } = useGetTutorialProgressQuery(undefined);
  return data?.tutorial_progress || {};
};
export const useTutorialById = (tutorialId: string) => {
  const { data } = useGetTutorialProgressQuery(undefined);

  return data?.tutorial_progress?.[tutorialId];
};

// User content hooks
export const useTemplatesList = (options?: { skip?: boolean }) => {
  const { data } = useGetTemplatesQuery(undefined, options);
  return data?.templates || [];
};
export const useTroubleWords = () => {
  const { data } = useGetTroubleWordsQuery(undefined);
  return data?.trouble_words || [];
};

// Preferences hooks
/** Minimal replacement for the old hook – returns a **primitive**. */
export const useSubjectLanguage = () => useSelector(selectSubjectLanguage);

/** New hook – returns **four** pre-computed variants in a memoised object. */
export const useSubjectLanguageVariants = () => useSelector(selectSubjectLanguageVariants);

export const useDefaultTimezone = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.default_timezone;
};

// Gamification and metrics hooks
export const useGamification = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.gamification;
};
export const useReferralInfo = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.referral_info;
};
export const useUserMetrics = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.user_metrics;
};

// Provider info hooks
export const useProviderBillingInfo = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.provider_billing_info;
};
export const usePatientMatchEnrolled = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.patient_match_enrolled || false;
};
export const useClearinghouseLinked = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.clearinghouse_linked;
};

// Stripe info hook
export const useStripeInfo = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.stripe_info;
};

// First login time hook
export const useFirstLoginTime = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data?.first_login_time;
};

// Full user state hook (for backward compatibility)
export const useFullUserState = () => {
  const { data } = useGetUserInfoQuery(undefined);
  return data || null;
};

// User state hook (returns the transformed userState object)
export const useUserState = (options?: { skip?: boolean }) => {
  const { data: userInfoData } = useGetUserInfoQuery(undefined, options);

  if (!userInfoData) return null;

  // Transform snake_case to camelCase for backward compatibility
  return {
    ...userInfoData,

    // Core properties
    user_id: userInfoData.user_id,
    userInfo: userInfoData.user_info,

    // Transform snake_case to camelCase
    featureFlags: userInfoData.feature_flags,
    paymentStatus: userInfoData.payment_status,
    emailVerified: userInfoData.email_verified,
    onboardingSteps: userInfoData.onboarding_steps,
    groupUserRoles: userInfoData.group_user_roles,
    groupIds: userInfoData.group_ids,
    gamification: userInfoData.gamification,
    referralInfo: userInfoData.referral_info,
    userMetrics: userInfoData.user_metrics,
    stripeInfo: userInfoData.stripe_info,
    lastVersionUsed: userInfoData.last_version_used,
    patientMatchEnrolled: userInfoData.patient_match_enrolled,
    providerBillingInfo: userInfoData.provider_billing_info,
    firstLoginTime: userInfoData.first_login_time,
    clearinghouseLinked: userInfoData.clearinghouse_linked,
    defaultTimezone: userInfoData.default_timezone,

    // Keep snake_case versions for gradual migration
    feature_flags: userInfoData.feature_flags,
    payment_status: userInfoData.payment_status,
    email_verified: userInfoData.email_verified,
    onboarding_steps: userInfoData.onboarding_steps,
    group_user_roles: userInfoData.group_user_roles,
    group_ids: userInfoData.group_ids,
    referral_info: userInfoData.referral_info,
    user_metrics: userInfoData.user_metrics,
    stripe_info: userInfoData.stripe_info,
    last_version_used: userInfoData.last_version_used,
    patient_match_enrolled: userInfoData.patient_match_enrolled,
    provider_billing_info: userInfoData.provider_billing_info,
    first_login_time: userInfoData.first_login_time,
    clearinghouse_linked: userInfoData.clearinghouse_linked,
    default_timezone: userInfoData.default_timezone,
  };
};

// Standalone hook for updating onboarding steps
export const useUpdateOnboardingStep = () => {
  const [updateOnboardingSteps] = useUpdateOnboardingStepsMutation();

  const updateOnboardingStep = useCallback(
    async (step: string, value: boolean) => {
      try {
        await updateOnboardingSteps({ [step]: value }).unwrap();
        // RTK Query will automatically update the cache
        return true;
      } catch (error) {
        handleApiError(error, 'Failed to update onboarding step');
        return false;
      }
    },
    [updateOnboardingSteps],
  );

  return updateOnboardingStep;
};

// Standalone hook for toggling feature flags
export const useToggleFeatureFlag = () => {
  const [toggleFeatureFlagMutation] = useToggleFeatureFlagMutation();

  const toggleFeatureFlag = useCallback(
    async (featureName: string, enable: boolean): Promise<boolean> => {
      try {
        await toggleFeatureFlagMutation({
          feature_name: featureName,
          enable,
        }).unwrap();
        return true;
      } catch (error) {
        handleApiError(error, `Error toggling feature flag ${featureName}`);
        return false;
      }
    },
    [toggleFeatureFlagMutation],
  );

  return toggleFeatureFlag;
};

/**
 * Hook to initialize scoped persistence once getUserInfo completes
 *
 * This hook:
 * - Watches for getUserInfo to succeed
 * - Calls initializeScopedPersistence with userId and groupIds
 * - Runs only once per app session
 *
 * Usage: Call this hook once at app root level
 */
export function useScopedPersistenceInit() {
  const { data: userInfo } = useGetUserInfoQuery(undefined);
  const initializedRef = useRef(false);

  useEffect(() => {
    // Only run once
    if (initializedRef.current) {
      return;
    }

    // Wait for user info to be available
    if (!userInfo?.user_id) {
      return;
    }

    // Import and call initialization
    import('@store/index')
      .then(({ initializeScopedPersistence }) => {
        initializeScopedPersistence(userInfo.user_id);
        initializedRef.current = true;
      })
      .catch(error => {
        console.error('[ScopedPersist] Failed to initialize:', error);
      });
  }, [userInfo]);
}
